import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import {
  getClassicLeague,
  getClassicNewEntries,
  getClassicStandings,
} from "core-integration/src/store/leagues/reducers";
import { fetchClassicLeagueStandings } from "core-integration/src/store/leagues/thunks";
import { getStartedPhases } from "core-integration/src/store/phases/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { formatRawAsLocalI18n } from "core-integration/src/utils/datetime";
import qs from "qs";
import React, { useEffect } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";
import { dateLocales } from "../../i18n";
import { getDisplayRoundNumber } from "../../utils/events";
import Alert from "../Alert";
import BoldLink from "../BoldLink";
import ButtonLink from "../ButtonLink";
import { InlineSelectField } from "../FieldRenderers";
import { ControlArrowLeft, ControlArrowRight } from "../icons/Arrows";
import { Main, Secondary, Wrapper } from "../Layout";
import Link from "../Link";
import { Pager, PagerItem, PagerItemNext } from "../Pager";
import Panel from "../Panel";
import SubHeading from "../SubHeading";
import Table from "../Table";
import Tooltip, { TooltipLabel } from "../Tooltip";
import Movement from "./Movement";
import StandingsHeading from "./StandingsHeading";
import StandingsRow from "./StandingsRow";
import { getCupUrl } from "./utils";

const StyledMain = styled(Main)`
  min-width: 100%;
  padding: ${({ theme }) => `${theme.space[4]} ${theme.space[2]}`};
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    padding: ${({ theme }) => `${theme.space[8]} ${theme.space[4]}`};
  }
`;

type Props = WithTranslation;

const StandingsClassic = ({ i18n, t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { leagueId } = useParams();

  // Get league id from path
  const leagueNumber = parseInt(leagueId!, 10) || 0;

  // Get phase and page numbers from query string
  const query = qs.parse(location!.search, {
    ignoreQueryPrefix: true,
  });

  const phase = parseInt(query.phase as string, 10) || 1;
  const pageNewEntries = parseInt(query.page_new_entries as string, 10) || 1;
  const pageStandings = parseInt(query.page_standings as string, 10) || 1;

  const now = useSelector(getCurrentEvent);
  const league = useSelector((state: RootState) =>
    getClassicLeague(state, leagueNumber),
  );
  const newEntries = useSelector((state: RootState) =>
    getClassicNewEntries(state, leagueNumber, pageNewEntries),
  );

  const phases = useSelector(getStartedPhases);
  const player = useSelector(getPlayerData);
  const standings = useSelector((state: RootState) =>
    getClassicStandings(state, leagueNumber, phase, pageStandings),
  );
  useEffect(() => {
    if (leagueNumber) {
      dispatch(
        fetchClassicLeagueStandings(
          leagueNumber,
          pageNewEntries,
          pageStandings,
          phase,
        ),
      );
    }
  }, [dispatch, leagueNumber, pageNewEntries, pageStandings, phase]);

  const buildLink = (
    phase: number,
    pageNewEntries: number,
    pageStandings: number,
  ) => {
    return `/leagues/${
      league!.id
    }/standings/c?phase=${phase}&page_new_entries=${pageNewEntries}&page_standings=${pageStandings}`;
  };

  const handleChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    navigate(buildLink(parseInt(e.currentTarget.value, 10), 1, 1));

  if (!league) {
    return null;
  }

  const started = now && league.start_event <= now.id;
  return (
    <>
      <Wrapper>
        <StyledMain>
          <StandingsHeading
            type="classic"
            league={league}
            leagueNumber={leagueNumber}
          />
          {league.has_cup && (
            <Box px={2} my={4}>
              <BoldLink to={getCupUrl(league.id, league.cup_league)}>
                {league.cup_league
                  ? t("standingClassic.matches", "View cup matches")
                  : t("standingClassic.info", "View cup info")}
              </BoldLink>
            </Box>
          )}
          {league.start_event > 1 && (
            <Box m={2}>
              <Alert type="info">
                {started
                  ? t(
                      "standingClassic.started",
                      "League scoring started in Round {{startEvent}}",
                      {
                        startEvent: getDisplayRoundNumber(league.start_event),
                      },
                    )
                  : t(
                      "standingClassic.starting",
                      "League scoring will start in Round {{startEvent}}",
                      {
                        startEvent: getDisplayRoundNumber(league.start_event),
                      },
                    )}
              </Alert>
            </Box>
          )}
          <Box m={2}>
            <form>
              <InlineSelectField
                id="selectPhase"
                name="selectPhase"
                label={t("standingClassic.selectPhase", "Select phase:  ")}
                onChange={handleChange}
                value={phase}
              >
                {phases.map((p) => (
                  <option
                    value={p.id}
                    key={p.id}
                    aria-selected={phase === p.id}
                  >
                    {p.name}
                  </option>
                ))}
              </InlineSelectField>
            </form>
          </Box>
          {standings && standings.lastUpdatedData && (
            <Box m={2}>
              {t(
                "standingClassic.lastUpdate",
                "Last updated: {{ lastUpdatedData }} (local time)",
                {
                  lastUpdatedData: formatRawAsLocalI18n(
                    standings.lastUpdatedData,
                    dateLocales[i18n.language],
                  ),
                },
              )}
            </Box>
          )}
          <Box mb={2}>
            <Panel>
              <Table>
                <thead>
                  <tr>
                    <th scope="col">{t("standingClassic.rank", "Rank")}</th>
                    <th scope="col">
                      {t("standingClassic.team", "Team & Manager")}
                    </th>
                    <th scope="col">
                      <Tooltip content="Round points">
                        <TooltipLabel>
                          {t("standingClassic.rd", "RD")}
                        </TooltipLabel>
                      </Tooltip>
                    </th>
                    <th scope="col">
                      <Tooltip content="Total points">
                        <TooltipLabel>
                          {t("standingClassic.tot", "TOT")}
                        </TooltipLabel>
                      </Tooltip>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {standings &&
                    standings.results.map((ls) => (
                      <StandingsRow
                        mine={player !== null && player.entry === ls.entry}
                        key={ls.entry}
                      >
                        <td>
                          <Flex alignItems="center">
                            <Box mr={1}>{ls.rank}</Box>
                            <Movement lastRank={ls.last_rank} rank={ls.rank} />
                          </Flex>
                        </td>
                        <td>
                          {now ? (
                            <Link to={`/entry/${ls.entry}/event/${now.id}`}>
                              <strong>{ls.entry_name}</strong>
                            </Link>
                          ) : (
                            <strong>{ls.entry_name}</strong>
                          )}
                          <br />
                          {ls.player_name}
                        </td>
                        <td>{ls.event_total}</td>
                        <td>{ls.total}</td>
                      </StandingsRow>
                    ))}
                </tbody>
              </Table>
            </Panel>
          </Box>
          {standings && (
            <Pager>
              <PagerItem>
                {pageStandings > 1 && (
                  <ButtonLink
                    to={buildLink(phase, pageNewEntries, pageStandings - 1)}
                    variant="primary"
                  >
                    <span>{t("standingClassic.previews", "Previous")}</span>
                  </ButtonLink>
                )}
              </PagerItem>
              <PagerItemNext>
                {standings.has_next && (
                  <ButtonLink
                    to={buildLink(phase, pageNewEntries, pageStandings + 1)}
                    variant="primary"
                  >
                    <span>{t("standingClassic.next", "Next")}</span>
                  </ButtonLink>
                )}
              </PagerItemNext>
            </Pager>
          )}
          {newEntries && newEntries.results.length > 0 && (
            <>
              <SubHeading>{t("standingClassic.new", "New entries")}</SubHeading>
              <Table>
                <thead>
                  <tr>
                    <th scope="col">{t("standingClassic.team1", "Team")}</th>
                    <th scope="col">
                      {t("standingClassic.manager1", "Manager")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {newEntries.results.map((ne) => (
                    <tr key={ne.entry}>
                      <td>{ne.entry_name}</td>
                      <td>
                        <Link to={`/entry/${ne.entry}/history`}>
                          {ne.player_first_name} {ne.player_last_name}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pager>
                <PagerItem>
                  {pageNewEntries > 1 && (
                    <ButtonLink
                      to={buildLink(phase, pageNewEntries - 1, pageStandings)}
                      variant="secondary"
                    >
                      <ControlArrowLeft />
                      <span>{t("standingClassic.previews1", "Previous")}</span>
                    </ButtonLink>
                  )}
                </PagerItem>
                <PagerItemNext>
                  {newEntries.has_next && (
                    <ButtonLink
                      to={buildLink(phase, pageNewEntries + 1, pageStandings)}
                      variant="secondary"
                    >
                      <Flex justify-content="space-between" alignItems="center">
                        <span>{t("standingClassic.next1", "Next")}</span>
                        <ControlArrowRight />
                      </Flex>
                    </ButtonLink>
                  )}
                </PagerItemNext>
              </Pager>
            </>
          )}
        </StyledMain>
        <Secondary>
          <></>
        </Secondary>
      </Wrapper>
    </>
  );
};

export default withTranslation()(StandingsClassic);
